import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  useLedgerAllocationOperationTypesQuery,
  useCreateLedgerAllocationOperationType,
  useUpdateLedgerAllocationOperationType,
  useDeleteLedgerAllocationOperationType,
  useMergeLedgerAllocationOperationType,
} from "../../hooks/useAccountingQueries";
import { extractApiErrorMessage } from "./utils";

type UseOperationTypesParams = {
  bankMovementId: string
  open: boolean
  selectedSimpleBudgetType: "ASC" | "AEP"
  onOperationTypeRemoved?: (operationTypeId: string) => void
};

export const useOperationTypes = ({
  bankMovementId,
  open,
  selectedSimpleBudgetType,
  onOperationTypeRemoved,
}: UseOperationTypesParams) => {
  const [isCategoryManagerOpen, setIsCategoryManagerOpen] = useState(false);
  const [categoryManagerFilter, setCategoryManagerFilter] = useState("");

  // ---------------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------------

  const {
    data: ascOperationTypes = [],
    isLoading: isLoadingAscOperationTypes,
  } = useLedgerAllocationOperationTypesQuery(
    { bankMovementId, budgetType: "ASC", actif: true },
    open && Boolean(bankMovementId),
  );

  const {
    data: aepOperationTypes = [],
    isLoading: isLoadingAepOperationTypes,
  } = useLedgerAllocationOperationTypesQuery(
    { bankMovementId, budgetType: "AEP", actif: true },
    open && Boolean(bankMovementId),
  );

  const {
    data: inactiveOperationTypes = [],
    isLoading: isLoadingInactiveOperationTypes,
  } = useLedgerAllocationOperationTypesQuery(
    { bankMovementId, budgetType: selectedSimpleBudgetType, actif: false },
    open && Boolean(bankMovementId) && isCategoryManagerOpen,
  );

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const createOperationTypeMutation = useCreateLedgerAllocationOperationType(bankMovementId);
  const updateOperationTypeMutation = useUpdateLedgerAllocationOperationType(bankMovementId, selectedSimpleBudgetType);
  const deleteOperationTypeMutation = useDeleteLedgerAllocationOperationType(bankMovementId, selectedSimpleBudgetType);
  const mergeOperationTypeMutation = useMergeLedgerAllocationOperationType(bankMovementId);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const operationTypes = selectedSimpleBudgetType === "AEP" ? aepOperationTypes : ascOperationTypes;
  const isLoadingOperationTypes = selectedSimpleBudgetType === "AEP" ? isLoadingAepOperationTypes : isLoadingAscOperationTypes;

  const operationTypesByBudget = useMemo(
    () => ({ ASC: ascOperationTypes, AEP: aepOperationTypes }),
    [ascOperationTypes, aepOperationTypes],
  );

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleDisableOperationType = async (operationTypeId: string) => {
    try {
      await updateOperationTypeMutation.mutateAsync({ operationTypeId, actif: false });
      onOperationTypeRemoved?.(operationTypeId);
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
  };

  const handleEnableOperationType = async (operationTypeId: string) => {
    try {
      await updateOperationTypeMutation.mutateAsync({ operationTypeId, actif: true });
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
  };

  const handleRenameOperationType = async (operationTypeId: string, name: string) => {
    try {
      await updateOperationTypeMutation.mutateAsync({ operationTypeId, name });
      toast.success("Catégorie renommée");
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
  };

  const handleUpdateAccountNumber = async (operationTypeId: string, numeroCompte: string | null) => {
    try {
      await updateOperationTypeMutation.mutateAsync({ operationTypeId, numeroCompte });
      toast.success(numeroCompte ? "Compte comptable enregistré" : "Compte comptable effacé");
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
  };

  const handleMergeOperationType = async (sourceId: string, targetId: string) => {
    try {
      await mergeOperationTypeMutation.mutateAsync({ operationTypeId: sourceId, targetOperationTypeId: targetId });
      onOperationTypeRemoved?.(sourceId);
      toast.success("Catégories fusionnées");
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
  };

  const handleDeleteOperationType = async (operationTypeId: string) => {
    try {
      await deleteOperationTypeMutation.mutateAsync(operationTypeId);
      onOperationTypeRemoved?.(operationTypeId);
    }
    catch (error) {
      toast.error(extractApiErrorMessage(error));
    }
  };

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    // Category manager UI
    isCategoryManagerOpen,
    setIsCategoryManagerOpen,
    categoryManagerFilter,
    setCategoryManagerFilter,

    // Operation types data
    ascOperationTypes,
    aepOperationTypes,
    operationTypes,
    isLoadingOperationTypes,
    operationTypesByBudget,
    inactiveOperationTypes,
    isLoadingInactiveOperationTypes,

    // Mutations (exposed for simple editor find-or-create)
    createOperationTypeMutation,

    // Busy flags
    isSimpleEditorBusy: createOperationTypeMutation.isPending
      || updateOperationTypeMutation.isPending
      || deleteOperationTypeMutation.isPending
      || mergeOperationTypeMutation.isPending,
    isCreateOperationTypePending: createOperationTypeMutation.isPending,

    // Handlers
    handleDisableOperationType: (id: string) => void handleDisableOperationType(id),
    handleEnableOperationType: (id: string) => void handleEnableOperationType(id),
    handleRenameOperationType: (id: string, name: string) => void handleRenameOperationType(id, name),
    handleUpdateAccountNumber: (id: string, numeroCompte: string | null) => void handleUpdateAccountNumber(id, numeroCompte),
    handleMergeOperationType: (sourceId: string, targetId: string) => void handleMergeOperationType(sourceId, targetId),
    handleDeleteOperationType: (id: string) => void handleDeleteOperationType(id),
  };
};
